// SPDX-License-Identifier: AGPL-3.0-only
/**
 * Sidebar: 256px rail — logo block + app-version chip and the five fixed nav
 * groups from the bootstrap NavTree (uppercase group labels, accent-soft
 * active state). Live unread badges: `badge: 'unread-count'` items render
 * the `/me/notifications` unread number (M7 T6), invalidated by the WS
 * `notifications:<userId>` channel.
 *
 * The rail is `sticky top-0 h-dvh` and only the middle `<nav>` scrolls. The
 * height is load-bearing: the shell parent is `min-h-dvh`, so without it the
 * aside stretches to *document* height, the inner `overflow-y-auto` never
 * engages, and the logo scrolls off the top of a long page.
 *
 * Multi-connection nav labels: once 2+ connections exist, each group's
 * items are sub-labeled by the owning connection's display name so
 * same-named tables from different sources stay unambiguous; with a single
 * connection the rail renders flat (no redundant label).
 */
import { Fragment } from 'react';
import { Link, useMatchRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  CalendarClock,
  Database,
  Download,
  FileChartColumn,
  History,
  KeyRound,
  Mail,
  Paperclip,
  ScrollText,
  ShieldCheck,
  Upload,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@adminium/ui';

import { unreadCountQuery } from '../api/notifications.js';
import { BrandMark, useBranding } from './BrandMark.js';
import {
  NAV_GROUP_KEYS,
  activeHostedItem,
  addOnNavOf,
  hostedAppsOf,
  type AddOnNavPage,
  type BootstrapData,
  type HostedApp,
  type NavGroupKey,
  type NavItem,
} from '../app/bootstrap.js';
import { t } from '../i18n/t.js';
import { lucideByName } from '../lib/lucide.js';
import { hasStudioAccess } from '../studio/StudioGuard.js';
import { distinctConnectionCount, sectionNavItems } from './navSections.js';

const GROUP_LABELS: Record<NavGroupKey, string> = {
  workspace: 'Workspace',
  library: 'Library',
  planning: 'Planning',
  people: 'People',
  account: 'Account',
};

/**
 * Group key → literal bundle key. The five A groups are fixed
 * (`NAV_GROUP_KEYS`), so `satisfies` makes a sixth group a compile error here
 * rather than a raw `nav.group.<new>` heading in the rail.
 */
const GROUP_LABEL_KEY = {
  workspace: 'nav.group.workspace',
  library: 'nav.group.library',
  planning: 'nav.group.planning',
  people: 'nav.group.people',
  account: 'nav.group.account',
} as const satisfies Record<NavGroupKey, string>;

export interface SidebarNavProps {
  bootstrap: BootstrapData;
  /**
   * Accepted but unused: the persona badge and its sign-out affordance live in
   * the topbar account menu now (`Topbar.tsx`, `topbar.signOut`). Kept so the
   * existing `AppShell` call site still typechecks until the prop is dropped
   * there too.
   */
  onSignOut?: (() => void) | undefined;
  className?: string | undefined;
}

/**
 * Static platform surfaces (M7 wave 2) — the the Engine does not seed into
 * `adminium_pages` yet (the same gap that makes `/imports`/`/exports` direct
 * routes; see data-io/routes.tsx). They render as a tail under their
 * ia-mapping A group so nav placement matches the spec today and the entries
 * can retire one-for-one as Engine seeding lands. `adminOnly` gates DISCOVERY
 * only (Email templates writes need `system:settings:manage`); the server
 * stays the security boundary.
 */
interface PlatformNavLink {
  to: string;
  labelKey: string;
  fallback: string;
  icon: LucideIcon;
  adminOnly?: boolean;
}

const PLATFORM_NAV: ReadonlyArray<{ group: NavGroupKey; links: readonly PlatformNavLink[] }> = [
  {
    group: 'library',
    links: [
      { to: '/imports', labelKey: 'nav.imports', fallback: 'Import data', icon: Upload },
      { to: '/exports', labelKey: 'nav.exports', fallback: 'Data exports', icon: Download },
      /*
       * `/files`. In `library` beside imports and exports because it is the
       * same kind of thing — a workspace-wide view of artifacts, not a page
       * over a customer table.
       *
       * `adminOnly` gates DISCOVERY only, as the comment above says: without
       * `files.manage` the page still works and shows the caller their OWN
       * uploads (the route is mine-only rather than forbidden), so hiding the
       * entry from non-admins costs them nothing they could see anyway while
       * keeping the rail short for the people who use the app rather than
       * administer it.
       */
      { to: '/files', labelKey: 'nav.files', fallback: 'Files', icon: Paperclip, adminOnly: true },
      {
        to: '/email-templates',
        labelKey: 'nav.emailTemplates',
        fallback: 'Email templates',
        icon: Mail,
        adminOnly: true,
      },
      /*
       * `/report-builder`: the comp's rail row is `{ key: 'reports', label:
       * 'Report builder', icon: 'file-bar-chart-2' }` (comp 123) — in the
       * comp's WORKSPACE group, which in Adminium's rail is the generated
       * pages'. It moves here, beside `/invoices`, the surface it mirrors
       * (Appendix A S1, the departure 34 S1 also took). NOT `/reports`, which
       * is Scheduled Reports in the `account` group below.
       */
      { to: '/report-builder', labelKey: 'nav.reportBuilder', fallback: 'Report builder', icon: FileChartColumn, adminOnly: true },
      /*
       * The comp's two adjacent Library rows (Automation Rules 370, Workflow
       * Logs 176). `adminOnly` gates DISCOVERY; the routes themselves are
       * behind `system:automations:manage` on the server, which is seeded to
       * super-admin only — so unlike `/files`, a non-holder who reached the
       * URL would see the 403 state rather than a narrowed page.
       */
      {
        to: '/automations',
        labelKey: 'nav.automations',
        fallback: 'Automations',
        icon: Workflow,
        adminOnly: true,
      },
      {
        to: '/workflow-logs',
        labelKey: 'nav.workflowLogs',
        fallback: 'Workflow logs',
        icon: History,
        adminOnly: true,
      },
    ],
  },
  {
    // The `people` group was a declared `NavGroupKey` with no links behind it
    // until user management landed — which is exactly what "a complete RBAC
    // engine with no way to reach it" looked like in the rail.
    group: 'people',
    links: [
      { to: '/settings/team', labelKey: 'nav.team', fallback: 'Team', icon: Users, adminOnly: true },
      {
        to: '/settings/roles',
        labelKey: 'nav.roles',
        fallback: 'Roles & permissions',
        icon: ShieldCheck,
        adminOnly: true,
      },
      /*
       * `/api-keys` shipped with a route, a 519-line page and no entry point in
       * any nav — the same shape the `people` comment above describes. It is
       * here rather than in `account` because a key is a PRINCIPAL, not a
       * personal preference: it is workspace-wide, it carries a role, and it
       * belongs beside the people and roles that can act. `adminOnly` mirrors
       * the route's own `StudioGuard`; the server's `system:api-keys:manage` is
       * the real boundary either way.
       */
      {
        to: '/api-keys',
        labelKey: 'nav.apiKeys',
        fallback: 'API keys',
        icon: KeyRound,
        adminOnly: true,
      },
      {
        to: '/audit',
        labelKey: 'nav.audit',
        fallback: 'Audit log',
        icon: ScrollText,
        adminOnly: true,
      },
    ],
  },
  {
    group: 'account',
    links: [
      {
        to: '/account/security',
        labelKey: 'nav.security',
        fallback: 'Password & sessions',
        icon: ShieldCheck,
      },
      {
        to: '/account/notifications',
        labelKey: 'nav.notificationSettings',
        fallback: 'Notification settings',
        icon: Bell,
      },
      {
        to: '/reports',
        labelKey: 'nav.scheduledReports',
        fallback: 'Scheduled reports',
        icon: CalendarClock,
      },
    ],
  },
];

/**
 * One row shared by the generated-page list and the platform-link tail — they
 * sit in the same rail and drifted apart by a pixel each time one was touched.
 * `group` is here for the unread badge, which recolours on the active row.
 *
 * The comp draws no hover state at all (it is a static mock); `--surface-2` is
 * kept because a nav with no hover feedback reads as inert.
 */
const NAV_LINK_CLASS =
  'group flex items-center gap-[11px] rounded-md px-[11px] py-2 text-[13.5px] font-semibold text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg data-[status=active]:bg-accent-soft data-[status=active]:font-bold data-[status=active]:text-accent';

function PlatformLinkList({ links }: { links: readonly PlatformNavLink[] }) {
  if (links.length === 0) return null;
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {links.map((link) => (
        <li key={link.to}>
          <Link to={link.to} className={NAV_LINK_CLASS}>
            <link.icon className="size-[18px] shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">{t(link.labelKey, link.fallback)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * An add-on's rail rows (51b).
 *
 * `/add-ons/$key/$` rather than a route per page: the set of pages is whatever
 * is installed, which a code-based route tree cannot know at build time. The
 * splat also carries a page's own sub-paths, so an editor at
 * `/add-ons/invoices/documents/42` is the page's business and not the router's.
 *
 * Icons resolve through `lucideByName` exactly as a generated page's do, so an
 * add-on naming an icon that does not exist gets the neutral glyph rather than
 * a crash.
 */
function AddOnLinkList({ pages }: { pages: readonly AddOnNavPage[] }) {
  if (pages.length === 0) return null;
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0" data-part="nav-add-on-pages">
      {pages.map((page) => {
        const Icon = lucideByName(page.icon);
        return (
          <li key={`${page.addOnKey}/${page.ref}`}>
            <Link
              to="/add-ons/$key/$"
              params={{ key: page.addOnKey, _splat: page.ref }}
              className={NAV_LINK_CLASS}
            >
              <Icon className="size-[18px] shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{t(page.labelKey, page.fallback)}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The `badge: 'unread-count'` live number: fed by `/me/notifications`'
 * `unreadCount` under the `['notifications']` query prefix, so WS
 * `notifications:<userId>` events (api/realtime.ts) keep it honest across
 * tabs; zero renders nothing. `pending-count` has no producer yet and is
 * deliberately not faked here.
 */
function UnreadCountBadge() {
  const count = useQuery(unreadCountQuery());
  if (count.data === undefined || count.data === 0) return null;
  return (
    <span
      data-part="nav-unread-badge"
      className="ms-auto rounded-full bg-surface-3 px-[7px] py-px text-[10.5px] font-bold text-fg-subtle tabular-nums group-data-[status=active]:bg-accent group-data-[status=active]:text-accent-fg"
    >
      {count.data > 99 ? '99+' : count.data}
    </span>
  );
}

function NavItemList({ items }: { items: readonly NavItem[] }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {items.map((item) => {
        const Icon = lucideByName(item.icon);
        return (
          <li key={item.pageId}>
            <Link to="/p/$slug" params={{ slug: item.slug }} className={NAV_LINK_CLASS}>
              <Icon className="size-[18px] shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{t(item.labelKey, item.fallback)}</span>
              {item.badge === 'unread-count' ? <UnreadCountBadge /> : null}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * One blended app's section.
 *
 * Same visual grammar as the five fixed groups — uppercase header, the same
 * row class — and deliberately NOT a sixth group. `NAV_GROUP_KEYS` is a closed
 * set with a `satisfies` that makes a sixth a compile error, and it should stay
 * closed: the five are the product's information architecture, while these are
 * whatever the operator happens to have installed.
 *
 * Active state is computed here rather than left to `Link`'s own matching, and
 * the reason is that neither of its modes is right. Exact matching would drop
 * the highlight the moment the app navigated to a screen it does not list (a
 * detail view); prefix matching would light up EVERY row, because an item whose
 * path is `''` — a surface whose only screen is its root — is a prefix of
 * everything. `activeHostedItem` is the app's own longest-match rule, shared with
 * the frame, so the highlight and the rendered screen cannot disagree.
 */
/**
 * The `$appKey` route param for a hosted section: the app key, or
 * `<appKey>~<instance>` for an extra tenant.
 *
 * ONE param rather than a second route segment, deliberately. A segment
 * (`/a/clients/berlin/…`) would sit exactly where the app's own paths live, so
 * `/a/clients/invoices` would mean two things; `~` cannot appear in an app key
 * or a slug, both of which are `[a-z0-9-]`, so this split is unambiguous.
 */
export function appKeyParam(app: Pick<HostedApp, 'appKey' | 'instance'>): string {
  return app.instance === undefined ? app.appKey : `${app.appKey}~${app.instance}`;
}

/** `<appKey>~<instance>` → its parts. The instance is null on the app's own section. */
export function splitAppKeyParam(param: string): { appKey: string; instance: string | null } {
  const at = param.indexOf('~');
  if (at === -1) return { appKey: param, instance: null };
  return { appKey: param.slice(0, at), instance: param.slice(at + 1) };
}

function HostedAppSection({ app }: { app: HostedApp }) {
  const matchRoute = useMatchRoute();
  const match = matchRoute({ to: '/a/$appKey/$', fuzzy: true });
  const here =
    match !== false && match.appKey === app.appKey
      ? (match._splat ?? '').replace(/^\/+|\/+$/g, '')
      : null;
  const active = here === null ? null : activeHostedItem(app, here);

  return (
    <div className="mb-1" data-part="nav-hosted-app">
      <div className="px-2 pb-1 pt-3 text-micro uppercase tracking-[0.06em] text-fg-subtle">
        {app.label}
      </div>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {app.items.map((item) => {
          const Icon = lucideByName(item.icon ?? 'file');
          return (
            <li key={item.id}>
              <Link
                to="/a/$appKey/$"
                params={{ appKey: appKeyParam(app), _splat: item.path }}
                className={NAV_LINK_CLASS}
                // The row's own `data-status`, not the router's — see above.
                data-status={active?.id === item.id ? 'active' : undefined}
              >
                <Icon className="size-[18px] shrink-0" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function SidebarNav({ bootstrap, className }: SidebarNavProps) {
  const { nav, version } = bootstrap;
  const { showVersion } = useBranding();
  // 2+ sources → sub-label items per connection; else stay flat.
  const multiConnection = distinctConnectionCount(nav) >= 2;
  const admin = hasStudioAccess(bootstrap.roles);
  const platformLinksFor = (group: NavGroupKey): readonly PlatformNavLink[] =>
    (PLATFORM_NAV.find((entry) => entry.group === group)?.links ?? []).filter(
      (link) => admin || link.adminOnly !== true,
    );
  /*
   * Add-on rows (51b). Filtered by the SAME rule the platform tail uses, so a
   * page an add-on marked `adminOnly` is invisible to a non-admin rather than
   * being a row that 403s when clicked.
   */
  const addOnNav = addOnNavOf(bootstrap);
  const visibleAddOnPages = addOnNav.pages.filter((page) => admin || !page.adminOnly);
  const addOnPagesFor = (group: string): readonly AddOnNavPage[] =>
    visibleAddOnPages.filter((page) => page.group === group);

  // Groups with only platform links or only add-on rows (no generated pages
  // yet) still render — a built-in group is drawn when ANYTHING lands in it.
  const navGroupKeys = new Set(nav.groups.map((group) => group.key));
  const platformOnlyGroups = PLATFORM_NAV.filter((entry) => !navGroupKeys.has(entry.group));
  const platformOnlyKeys = new Set(platformOnlyGroups.map((entry) => entry.group));
  /*
   * A built-in group an add-on landed in that neither of the two lists above
   * would have drawn: no generated pages, and no platform links either
   * (`planning` and `people` are empty on a stock instance). Without this the
   * page installs, the rail has no heading to hang it under, and the row
   * silently does not exist.
   */
  const addOnOnlyBuiltinGroups = NAV_GROUP_KEYS.filter(
    (key) =>
      !navGroupKeys.has(key) && !platformOnlyKeys.has(key) && addOnPagesFor(key).length > 0,
  );
  /*
   * The trailing band of groups an add-on brought: built-in groups keep their
   * order, and an add-on cannot push one of them down the rail. A group whose
   * every row is `adminOnly` renders NOTHING for a viewer who is not an admin —
   * the empty heading is the one thing the rail must not draw, and this is the
   * layer that can see it, because it is the layer that filtered the rows.
   */
  const declaredGroups = addOnNav.groups.filter(
    (group) => addOnPagesFor(group.key).length > 0,
  );

  /*
   * Blended apps sit AFTER `workspace` (D7): they are the operator's daily
   * work, so burying them under Library/Planning/People/Account would put the
   * thing this wave exists to surface at the bottom of the rail.
   *
   * When there is no `workspace` group at all — a fresh instance with no
   * connection — they render first instead of vanishing, which is the case
   * that matters most: an operator whose ONLY installed thing is an app.
   */
  const hostedApps = hostedAppsOf(bootstrap);
  const hostedSections =
    hostedApps.length === 0
      ? null
      : hostedApps.map((app) => <HostedAppSection key={app.appKey} app={app} />);
  const hostedAfter = nav.groups.some((group) => group.key === 'workspace') ? 'workspace' : null;

  return (
    <aside
      data-part="sidebar"
      className={cn(
        'sticky top-0 flex h-dvh w-sidebar shrink-0 flex-col border-e border-border bg-surface',
        className,
      )}
    >
      {/*
        The aside stays unpadded so the nav's scrollbar rides flush against the
        border; every child carries the design's *effective* inset instead
        (rail 14px + child 8px = 22px, top 18px + 8px = 26px).
      */}
      <div className="flex items-center gap-2.5 px-[22px] pb-3.5 pt-[26px]">
        <BrandMark glow className="min-w-0" />
        {/* The version chip is opt-out (`branding.showVersion`): knowing which
            build you are on is a support handshake, but a white-labelled
            deployment is entitled not to advertise what it is built on. */}
        {showVersion ? (
          <span
            data-part="sidebar-version"
            className="ms-auto shrink-0 rounded-sm border border-border px-1.5 py-0.5 text-[10px] font-bold text-fg-subtle"
          >
            v{version}
          </span>
        ) : null}
      </div>

      {/* Nav groups */}
      <nav aria-label={t('nav.primary', 'Primary')} className="nb-scroll min-h-0 flex-1 overflow-y-auto px-2.5 py-2">
        {/*
          * The platform tail is NOT conditional on having generated pages.
          * This used to be an either/or: with no `nav.groups` the rail rendered
          * the prompt below (or the hosted apps) and returned, which skipped
          * `platformOnlyGroups` entirely — so a fresh instance hid Team, Roles,
          * API keys, the audit log, Files, Email templates, imports, exports
          * and scheduled reports behind a database connection none of them need.
          * The first thing an admin does is invite people, and Team was
          * unreachable until they had connected a source first.
          *
          * So the three blocks below always run and each contributes what it
          * has. The prompt is now only what it says it is — an explanation for
          * the missing PAGES — and shows when there is genuinely nothing
          * generated and no app either.
          */}
        {hostedAfter === null ? hostedSections : null}
        {nav.groups.length === 0 && hostedApps.length === 0 ? (
          <p className="px-2 py-3 text-body-sm text-fg-subtle">
            {t('nav.empty', 'Pages appear here once a database is connected.')}
          </p>
        ) : null}
        {nav.groups.map((group) => (
              <Fragment key={group.key}>
              <div className="mb-1">
                <div className="px-2 pb-1 pt-3 text-micro uppercase tracking-[0.06em] text-fg-subtle">
                  {t(GROUP_LABEL_KEY[group.key], GROUP_LABELS[group.key])}
                </div>
                {multiConnection ? (
                  sectionNavItems(group.items).map((section) => (
                    <div key={section.connectionId ?? 'shared'} className="mb-1.5">
                      <div
                        data-part="nav-connection-label"
                        className="flex items-center gap-1.5 px-2.5 pb-0.5 pt-1 text-micro font-bold text-fg-subtle"
                      >
                        <Database className="size-3 shrink-0" aria-hidden="true" />
                        <span className="min-w-0 truncate">
                          {section.connectionId === null
                            ? t('nav.connection.shared', 'Shared')
                            : (section.connectionName ??
                              t('nav.connection.unnamed', 'Connection'))}
                        </span>
                      </div>
                      <NavItemList items={section.items} />
                    </div>
                  ))
                ) : (
                  <NavItemList items={group.items} />
                )}
                <PlatformLinkList links={platformLinksFor(group.key)} />
                <AddOnLinkList pages={addOnPagesFor(group.key)} />
              </div>
              {hostedAfter === group.key ? hostedSections : null}
              </Fragment>
        ))}
        {platformOnlyGroups.map((entry) => {
          const links = platformLinksFor(entry.group);
          const pages = addOnPagesFor(entry.group);
          if (links.length === 0 && pages.length === 0) return null;
          return (
            <div key={entry.group} className="mb-1" data-part="nav-platform-group">
              <div className="px-2 pb-1 pt-3 text-micro uppercase tracking-[0.06em] text-fg-subtle">
                {t(GROUP_LABEL_KEY[entry.group], GROUP_LABELS[entry.group])}
              </div>
              <PlatformLinkList links={links} />
              <AddOnLinkList pages={pages} />
            </div>
          );
        })}
        {addOnOnlyBuiltinGroups.map((key) => (
          <div key={key} className="mb-1" data-part="nav-platform-group">
            <div className="px-2 pb-1 pt-3 text-micro uppercase tracking-[0.06em] text-fg-subtle">
              {t(GROUP_LABEL_KEY[key], GROUP_LABELS[key])}
            </div>
            <AddOnLinkList pages={addOnPagesFor(key)} />
          </div>
        ))}
        {declaredGroups.map((group) => (
          <div key={group.key} className="mb-1" data-part="nav-add-on-group">
            <div className="px-2 pb-1 pt-3 text-micro uppercase tracking-[0.06em] text-fg-subtle">
              {t(group.labelKey, group.fallback)}
            </div>
            <AddOnLinkList pages={addOnPagesFor(group.key)} />
          </div>
        ))}
      </nav>
    </aside>
  );
}
